/* eslint-disable no-plusplus */
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxSection } from 'components/_new/Box';
import { UpdateQuestionSettingsPayload } from 'api/survey';
import { SurveyProgramQuestionBase } from 'types/Survey';
import {
	DragDropContext,
	Droppable,
	Draggable,
	DropResult,
	SnapDragActions,
	SensorAPI,
	PreDragActions,
} from 'react-beautiful-dnd';
import { Button } from 'components/_new/Button';
import { Tag } from 'components/_new/Tag';
import { Skeleton } from 'primereact/skeleton';
import { QuestionItem } from './QuestionItem';
import { VirtualStepAdder } from './VIrtualStepAdder';
import { QuestionPlacement } from '../types';

type StandardViewProps = {
	localQuestionsState: [
		SurveyProgramQuestionBase[],
		React.Dispatch<React.SetStateAction<SurveyProgramQuestionBase[]>>
	];
	onUpdateQuestionSettings: (data: { questionId: number; data: UpdateQuestionSettingsPayload }) => void;
	disableActions: boolean;
	onEdit: (questionId: number) => void;
	onDelete: (questionId: number) => void;
	onAddQuestion: (placement: QuestionPlacement) => void;
	isLoading: boolean;
};

export const StandardView = ({
	localQuestionsState,
	onUpdateQuestionSettings,
	disableActions,
	onEdit,
	onDelete,
	onAddQuestion,
	isLoading,
}: StandardViewProps) => {
	const { t } = useTranslation();

	const [localQuestions, setLocalQuestions] = localQuestionsState;

	const [isDragging, setIsDragging] = useState(false);
	const [isControlDragging, setIsControlDragging] = useState(false);
	const [virtualStep, setVirtualStep] = useState<number | null>(null);

	const grouppedQuestions = useMemo(() => {
		const items =
			localQuestions
				.filter((q) => !q.isExcluded)
				.reduce<Record<number, SurveyProgramQuestionBase[]>>((current, item) => {
					if (current[item.inProgramStep]) {
						current[item.inProgramStep].push(item);
					} else {
						// eslint-disable-next-line no-param-reassign
						current[item.inProgramStep] = [item];
					}
					return current;
				}, {}) || {};

		if (virtualStep !== null && !items[virtualStep]) {
			items[virtualStep] = [];
		}

		return items;
	}, [localQuestions, virtualStep]);

	const excludedQuestions = useMemo(() => localQuestions.filter((q) => q.isExcluded), [localQuestions]);

	const onDragEnd = useCallback(
		(result: DropResult) => {
			setIsDragging(false);
			setIsControlDragging(false);

			const { source, destination } = result;

			if (
				!destination ||
				(source.droppableId === destination.droppableId && source.index === destination.index)
			) {
				return;
			}

			const sourceStep = Number(source.droppableId.split('-')[1]);
			const destinationStep = Number(destination.droppableId.split('-')[1]);
			const isDestinationVirtual = virtualStep === destinationStep;

			setLocalQuestions((prevQuestions) => {
				if (sourceStep === destinationStep) {
					const stepQuestions = prevQuestions.filter((q) => q.inProgramStep === sourceStep);
					const otherQuestions = prevQuestions.filter((q) => q.inProgramStep !== sourceStep);

					stepQuestions.sort((a, b) => a.inProgramSequence - b.inProgramSequence);
					const [movedQuestion] = stepQuestions.splice(source.index, 1);
					stepQuestions.splice(destination.index, 0, movedQuestion);

					const updatedStepQuestions = stepQuestions.map((question, index) => ({
						...question,
						inProgramSequence: index + 1,
					}));

					onUpdateQuestionSettings({
						questionId: movedQuestion.id,
						data: {
							inProgramSequence: destination.index + 1,
						},
					});

					return [...otherQuestions, ...updatedStepQuestions];
				}

				if (isDestinationVirtual) {
					const sourceStepQuestions = prevQuestions
						.filter((q) => q.inProgramStep === sourceStep)
						.sort((a, b) => a.inProgramSequence - b.inProgramSequence);

					const [movedQuestion] = sourceStepQuestions.splice(source.index, 1);

					const updatedMovedQuestion = {
						...movedQuestion,
						step: 1,
						inProgramStep: 1,
						inProgramSequence: 1,
					};

					const updatedOtherQuestions = prevQuestions
						.filter((q) => q.id !== movedQuestion.id)
						.map((q) => ({
							...q,
							step: q.step + 1,
							inProgramStep: q.inProgramStep + 1,
						}));

					onUpdateQuestionSettings({
						questionId: movedQuestion.id,
						data: {
							only: true,
							inProgramStep: updatedMovedQuestion.inProgramStep,
						},
					});

					setVirtualStep(null);

					return [updatedMovedQuestion, ...updatedOtherQuestions];
				}

				const sourceStepQuestions = prevQuestions
					.filter((q) => q.inProgramStep === sourceStep)
					.sort((a, b) => a.inProgramSequence - b.inProgramSequence);

				const destinationStepQuestions = prevQuestions
					.filter((q) => q.inProgramStep === destinationStep)
					.sort((a, b) => a.inProgramSequence - b.inProgramSequence);

				const otherQuestions = prevQuestions.filter(
					(q) => q.inProgramStep !== sourceStep && q.inProgramStep !== destinationStep
				);

				const [movedQuestion] = sourceStepQuestions.splice(source.index, 1);

				const updatedMovedQuestion = {
					...movedQuestion,
					inProgramStep: destinationStep,
					inProgramSequence: destination.index + 1,
				};

				destinationStepQuestions.splice(destination.index, 0, updatedMovedQuestion);

				onUpdateQuestionSettings({
					questionId: movedQuestion.id,
					data: {
						inProgramStep: destinationStep,
						inProgramSequence: destination.index + 1,
					},
				});

				const updatedSourceStepQuestions = sourceStepQuestions.map((q, index) => ({
					...q,
					inProgramSequence: index + 1,
				}));
				const updatedDestinationStepQuestions = destinationStepQuestions.map((q, index) => ({
					...q,
					inProgramSequence: index + 1,
				}));
				const updated = [...otherQuestions, ...updatedSourceStepQuestions, ...updatedDestinationStepQuestions];

				return updated;
			});
		},
		[onUpdateQuestionSettings, setLocalQuestions, virtualStep]
	);

	const onDragStart = useCallback(() => {
		setIsDragging(true);
	}, []);

	const handleAddStep = useCallback(
		(afterStep: number | 'start' | 'end') => {
			const allSteps = Object.keys(grouppedQuestions)
				.filter((s) => Number(s) !== virtualStep)
				.map(Number)
				.sort((a, b) => a - b);

			let newStep: number;

			if (afterStep === 'start') {
				const minStep = allSteps.length > 0 ? Math.min(...allSteps) : 1;
				newStep = minStep - 1;
			} else if (afterStep === 'end') {
				const maxStep = allSteps.length > 0 ? Math.max(...allSteps) : 0;
				newStep = maxStep + 1;
			} else {
				const realStepsAfter = allSteps.filter((s) => s > afterStep);
				const nextRealStep = realStepsAfter.length > 0 ? Math.min(...realStepsAfter) : null;
				if (nextRealStep === null) {
					const maxStep = allSteps.length > 0 ? Math.max(...allSteps) : 0;
					newStep = maxStep + 1;
				} else {
					newStep = (afterStep + nextRealStep) / 2;
				}
			}

			setVirtualStep(newStep);
		},
		[grouppedQuestions, virtualStep]
	);

	const handleAddQuestion = useCallback(
		(step: number, sequence: number) => {
			const only = virtualStep === step;
			if (only) {
				onAddQuestion({
					step: Math.ceil(step === 0 ? 1 : 0),
					sequence,
					only: true,
				});
			} else {
				onAddQuestion({
					step,
					sequence,
					only: false,
				});
			}
		},
		[onAddQuestion, virtualStep]
	);

	const handleRemoveVirtualStep = useCallback((step: number) => {
		setVirtualStep(null);
	}, []);

	const stepDisplayMapping = useMemo(() => {
		const allSteps = Object.keys(grouppedQuestions)
			.map(Number)
			.sort((a, b) => a - b);
		const mapping: Record<number, number> = {};
		allSteps.forEach((step, index) => {
			mapping[step] = index + 1;
		});
		return mapping;
	}, [grouppedQuestions]);

	const maxSteps = Object.keys(grouppedQuestions).length;

	const hasVirtualFirstStep = useMemo(() => {
		const allSteps = Object.keys(grouppedQuestions)
			.map(Number)
			.sort((a, b) => a - b);
		if (allSteps.length === 0) return false;
		const firstStep = allSteps[0];
		return virtualStep === firstStep;
	}, [grouppedQuestions, virtualStep]);

	const hasVirtualLastStep = useMemo(() => {
		const allSteps = Object.keys(grouppedQuestions)
			.map(Number)
			.sort((a, b) => a - b);
		if (allSteps.length === 0) return false;
		const lastStep = allSteps[allSteps.length - 1];
		return virtualStep === lastStep;
	}, [grouppedQuestions, virtualStep]);

	const isVirtualStepBetween = useMemo(() => {
		const allRealSteps = Object.keys(grouppedQuestions)
			.filter((s) => Number(s) !== virtualStep)
			.map(Number)
			.sort((a, b) => a - b);

		const result: Record<number, boolean> = {};

		for (let i = 0; i < allRealSteps.length - 1; i++) {
			const currentRealStep = allRealSteps[i];
			const nextRealStep = allRealSteps[i + 1];

			const hasVirtualBetween =
				virtualStep !== null && virtualStep > currentRealStep && virtualStep < nextRealStep;
			result[currentRealStep] = hasVirtualBetween;
		}

		return result;
	}, [grouppedQuestions, virtualStep]);

	const sensorAPIRef = useRef<SensorAPI>(null);
	const lift = useCallback(
		(quoteId: string): SnapDragActions | null => {
			if (isDragging) {
				return null;
			}

			const api: SensorAPI | null = sensorAPIRef.current;
			if (!api) {
				return null;
			}

			const preDrag: PreDragActions | null = api.tryGetLock(quoteId, () => {});
			if (!preDrag) {
				return null;
			}

			setIsControlDragging(true);
			return preDrag.snapLift();
		},
		[isDragging]
	);

	const allSortedSteps = useMemo(() => {
		return Object.keys(grouppedQuestions)
			.map(Number)
			.sort((a, b) => a - b);
	}, [grouppedQuestions]);

	const handleMoveQuestion = useCallback(
		(questionId: number, direction: 'up' | 'down') => {
			const question = localQuestions.find((q) => q.id === questionId);
			if (!question) return;

			const currentStep = question.inProgramStep;
			const stepQuestions = localQuestions
				.filter((q) => q.inProgramStep === currentStep)
				.sort((a, b) => a.inProgramSequence - b.inProgramSequence);

			const currentIndex = stepQuestions.findIndex((q) => q.id === questionId);
			const isFirst = currentIndex === 0;
			const isLast = currentIndex === stepQuestions.length - 1;

			const currentStepIndex = allSortedSteps.indexOf(currentStep);
			const isFirstStep = currentStepIndex === 0;
			const isLastStep = currentStepIndex === allSortedSteps.length - 1;

			if (direction === 'up') {
				if (isFirst) {
					if (isFirstStep) {
						// case 1 - move first question in first step to up - move to new first step
						const updatedMovedQuestion = {
							...question,
							step: 1,
							inProgramStep: 1,
							inProgramSequence: 1,
						};
						onUpdateQuestionSettings({
							questionId,
							data: {
								inProgramStep: 1,
								only: true,
							},
						});
						setVirtualStep(null); // always remove virtual step
						setLocalQuestions((prev) => {
							const updatedOtherQuestions = prev
								.filter((q) => q.id !== questionId)
								.map((q) => ({
									...q,
									step: q.step + 1,
									inProgramStep: q.inProgramStep + 1,
								}));

							return [updatedMovedQuestion, ...updatedOtherQuestions];
						});
					}

					// case 2 - move first question in non-first step to up - move to upper step as last item sequence
					else {
						// maybe new virtual step exists case
						const prevStep = allSortedSteps[currentStepIndex - 1];
						if (prevStep !== undefined) {
							// remove virtual step if it matches prevStep
							if (virtualStep === prevStep) {
								setVirtualStep(null);
							}
							onUpdateQuestionSettings({
								questionId,
								data: {
									inProgramStep: prevStep,
								},
							});
							setLocalQuestions((prev) => {
								const prevStepQuestions = prev.filter((q) => q.inProgramStep === prevStep);
								const newQuestionSequence =
									Math.max(...prevStepQuestions.map((q) => q.inProgramSequence)) + 1;
								const updatedMovedQuestion = {
									...question,
									step: prevStep,
									inProgramStep: prevStep,
									inProgramSequence: newQuestionSequence,
								};
								return [
									...prev.filter(
										(q) =>
											q.id !== questionId &&
											(q.inProgramStep < prevStep ||
												(q.inProgramStep === prevStep &&
													q.inProgramSequence < newQuestionSequence))
									),
									updatedMovedQuestion,
									...prev.filter((q) => q.id !== questionId && q.inProgramStep > prevStep),
								];
							});
						}
					}
					return;
				}
			} else if (direction === 'down') {
				if (isLast) {
					if (isLastStep) {
						// case 3 - move last question in last step to down - move to new last step
						onUpdateQuestionSettings({
							questionId,
							data: {
								inProgramStep: maxSteps + 1,
								only: true,
							},
						});
						setVirtualStep(null); // always remove virtual step
						setLocalQuestions((prev) => {
							const updatedMovedQuestion = {
								...question,
								step: maxSteps + 1,
								inProgramStep: maxSteps + 1,
								inProgramSequence: 1,
							};

							return [...prev.filter((q) => q.id !== questionId), updatedMovedQuestion];
						});
					}

					// case 4 - move last question in non-last step to down - move to down step as first item sequence
					else {
						// maybe new virtual step exists case
						const nextStep = allSortedSteps[currentStepIndex + 1];
						if (nextStep !== undefined) {
							// remove virtual step if it matches nextStep
							if (virtualStep === nextStep) {
								setVirtualStep(null);
							}
							onUpdateQuestionSettings({
								questionId,
								data: {
									inProgramStep: nextStep,
								},
							});
							setLocalQuestions((prev) => {
								const updatedMovedQuestion = {
									...question,
									step: nextStep,
									inProgramStep: nextStep,
									inProgramSequence: 1,
								};

								const updatedOtherQuestions = prev
									.filter((q) => q.id !== questionId && q.inProgramStep === nextStep)
									.map((q) => ({
										...q,
										inProgramSequence: q.inProgramSequence + 1,
									}));

								return [
									...prev.filter((q) => q.id !== questionId && q.inProgramStep < nextStep),
									updatedMovedQuestion,
									...updatedOtherQuestions,
									...prev.filter((q) => q.id !== questionId && q.inProgramStep > nextStep),
								];
							});
						}
					}
					return;
				}
			}

			const lifted = lift(`question-${questionId}`);
			if (lifted) {
				if (direction === 'up') {
					lifted.moveUp();
				} else {
					lifted.moveDown();
				}
				setTimeout(() => {
					lifted.drop();
				}, 100);
			}
		},
		[localQuestions, allSortedSteps, lift, onUpdateQuestionSettings, maxSteps, virtualStep]
	);

	if (isLoading) {
		return (
			<div className="flex flex-column gap-4 mt-6">
				<Skeleton className="w-full" height="300px" />
				<Skeleton className="w-full" height="400px" />
			</div>
		);
	}

	return (
		<DragDropContext
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			sensors={[
				(api) => {
					(sensorAPIRef as any).current = api;
				},
			]}
		>
			{/* {!hasVirtualFirstStep && (
				<div className="flex flex-row justify-content-center">
					<Button
						label={t('programPanel.feedback.addStepAtBeginning')}
						icon="plus"
						variant="secondary-outlined"
						variantSize="sm"
						onClick={() => handleAddStep('start')}
						disabled={isDragging}
					/>
				</div>
			)} */}
			{Object.entries(grouppedQuestions)
				.sort(([a], [b]) => Number(a) - Number(b))
				.map(([step, questions]) => {
					const stepNum = Number(step);
					const displayStep = stepDisplayMapping[stepNum];
					const isFirstStep = displayStep === 1;
					const isLastStep = displayStep === maxSteps;
					const questionLastIndex = questions.length - 1;
					const isVirtualStep = virtualStep === stepNum;

					return (
						<Box key={`step-${step}`} variant="blue-bordered" className="relative">
							<Droppable droppableId={`step-${step}`}>
								{(provided) => (
									<BoxSection
										ref={provided.innerRef}
										{...provided.droppableProps}
										header={
											<div className="flex flex-row justify-content-between align-items-center w-full">
												<div className="flex flex-row gap-2 align-items-center">
													<span>Krok {displayStep}</span>
													{isVirtualStep ? (
														<Tag variant="blue" min>
															{t('programPanel.feedback.newStep')}
														</Tag>
													) : (
														<Tag variant="grey" min>
															{t('programPanel.feedback.questionsCount', {
																count: questions.length,
															})}
														</Tag>
													)}
												</div>
											</div>
										}
										contentClassName="gap-0"
									>
										<div className="flex flex-row justify-content-center gap-2 mb-3">
											{questions.length > 0 ? (
												<Button
													label={t('programPanel.feedback.addQuestionAtBeginning')}
													icon="plus"
													variant="primary-outlined"
													variantSize="sm"
													onClick={() => handleAddQuestion(Number(step), 1)}
													disabled={disableActions}
												/>
											) : (
												<>
													<Button
														label={t('programPanel.feedback.addQuestion')}
														icon="plus"
														variant="primary-outlined"
														variantSize="sm"
														onClick={() => handleAddQuestion(Number(step), 1)}
														disabled={disableActions}
													/>
													{/* {isVirtualStep && (
														<Button
															label={t('programPanel.feedback.removeStep')}
															icon="trash"
															variant="danger-text"
															variantSize="sm"
															onClick={() => handleRemoveVirtualStep(stepNum)}
															disabled={disableActions}
														/>
													)} */}
												</>
											)}
										</div>
										{questions.map((question, index) => {
											const { id } = question;
											const isFirstInStep = index === 0;
											const isLastInStep = index === questionLastIndex;
											const isOnlyInStep = questions.length === 1;

											return (
												<Draggable
													key={`question-${id}`}
													draggableId={`question-${id}`}
													index={index}
												>
													{(provided, snapshot) => {
														const style = provided.draggableProps.style as any;
														const draggingStyle = snapshot.isDragging
															? {
																	...style,
																	left: 'auto',
																	top: style?.top,
															  }
															: style;

														return (
															<QuestionItem
																ref={provided.innerRef}
																{...provided.draggableProps}
																style={draggingStyle}
																className="mb-3"
																no={index + 1}
																question={question}
																dragHandleProps={provided.dragHandleProps}
																isDragging={snapshot.isDragging && !isControlDragging}
																onEdit={onEdit}
																onDelete={onDelete}
																onMove={handleMoveQuestion}
																disableActions={disableActions}
																moveProps={{
																	up: {
																		// icon: isFirstInStep
																		// 	? 'level-up-alt'
																		// 	: undefined,
																		// iconClassName:
																		// 	isFirstStep && isFirstInStep
																		// 		? 'text-rose'
																		// 		: undefined,
																		// disabled:
																		// 	isFirstStep &&
																		// 	isFirstInStep &&
																		// 	isOnlyInStep,
																		disabled: isFirstStep && isFirstInStep,
																	},
																	down: {
																		// icon: isLastInStep
																		// 	? 'level-down-alt'
																		// 	: undefined,
																		// iconClassName:
																		// 	isLastStep && isLastInStep
																		// 		? 'text-rose'
																		// 		: undefined,
																		// disabled:
																		// 	isLastStep && isLastInStep && isOnlyInStep,
																		disabled: isLastStep && isLastInStep,
																	},
																}}
															/>
														);
													}}
												</Draggable>
											);
										})}

										{provided.placeholder}

										{questions.length > 0 && (
											<div className="flex flex-row justify-content-center">
												<Button
													label={t('programPanel.feedback.addQuestionAtEnd')}
													icon="plus"
													variant="primary-outlined"
													variantSize="sm"
													onClick={() =>
														handleAddQuestion(Number(step), questions.length + 1)
													}
													disabled={disableActions}
												/>
											</div>
										)}
									</BoxSection>
								)}
							</Droppable>

							{/* {!isLastStep && !isVirtualStep && !isVirtualStepBetween[stepNum] && (
								<VirtualStepAdder afterStep={stepNum} onAdd={handleAddStep} disabled={disableActions} />
							)} */}
						</Box>
					);
				})}
			{/* {!hasVirtualLastStep && (
				<div className="flex flex-row justify-content-center">
					<Button
						label={t('programPanel.feedback.addStepAtEnd')}
						icon="plus"
						variant="secondary-outlined"
						variantSize="sm"
						onClick={() => handleAddStep('end')}
						disabled={disableActions}
					/>
				</div>
			)} */}

			<Box variant="blue-bordered" className="relative">
				<BoxSection
					header={
						<div className="flex flex-row justify-content-between align-items-center w-full">
							<div className="flex flex-row gap-2 align-items-center">
								<span>{t('programPanel.feedback.excluded')}</span>
								<Tag variant="grey" min>
									{excludedQuestions.length}
								</Tag>
							</div>
						</div>
					}
					contentClassName="gap-0"
				>
					{excludedQuestions.length > 0 ? (
						excludedQuestions.map((question, index) => (
							<QuestionItem
								key={question.id}
								className="mb-3"
								no={index + 1}
								question={question}
								onEdit={onEdit}
								onDelete={onDelete}
								disableActions={disableActions}
							/>
						))
					) : (
						<div className="flex flex-row justify-content-center p-3 text-gray">
							{t('programPanel.feedback.noExcludedQuestions')}
						</div>
					)}
				</BoxSection>
			</Box>
		</DragDropContext>
	);
};
